import React, { useRef, useEffect } from "react";
import { useState } from "react";
import { useRecordWebcam, CAMERA_STATUS } from "react-record-webcam";
import { useInterval } from "../../hooks/useInternval";
import { useKeyPress } from "../../hooks/useKeyPress";
import { drawLastHumanCanvas } from "../../logic/drawLastHumanCanvas";
import CountdownToRecord from "../countdownToRecord/CountdownToRecord";
import RecordingIndicator from "../recordingIndicator/RecordingIndicator";
import styles from "./webcamMirror.module.css";
import introAudio from "../../audio/intro-audio.mp3";

// My 2K webcam used on HAL supports these resolutions
// 2048×1536
// 640×480

/*
Keep pressing record over and over again.
[Intervention] Blocked attempt to create a WebMediaPlayer as there are too many WebMediaPlayers already in existence. See crbug.com/1144736#c27
*/

export const WebcamMirror = ({ settings }) => {
  const [resetMinsRemaining, setResetMinsRemaining] = useState(null);
  const [showRecordAndPreviewVids, setShowRecordAndPreviewVids] =
    useState(false);
  const [recordTimeRemaining, setRecordTimeRemaining] = useState(null);
  const [countdownToRecord, setCountdownToRecord] = useState(null);
  const recordWebcam = useRecordWebcam(settings.OPTIONS);

  const demoVidRef = useRef(null); // demo video
  const frameCanvas = useRef(null);
  const canvasRef = useRef(null);
  const audioRef = useRef(null);
  const currTimeoutRef = useRef(null);
  const resetTimeoutRef = useRef(null);

  useKeyPress("p", () => onTrigger("intro"));
  useKeyPress("r", () => onTrigger("record"));
  useKeyPress("d", () => onTrigger("demo"));

  const onTrigger = (trigger) => {
    if (trigger === "intro") {
      const wasNotPlaying = audioRef.current.paused;
      resetAll();
      if (wasNotPlaying) {
        onPlayIntroAudio();
      }
    } else if (trigger === "record") {
      resetAll();
      onRecordStart();
    } else if (trigger === "demo") {
      resetAll();
      window.location.reload();
    }
  };

  const resetAll = () => {
    if (currTimeoutRef.current) {
      clearTimeout(currTimeoutRef.current);
    }
    if (resetTimeoutRef.current) {
      clearTimeout(resetTimeoutRef.current);
    }
    stopAudioIfPlaying();
    setShowRecordAndPreviewVids(false);
    setRecordTimeRemaining(null);
    setCountdownToRecord(null);
    setResetMinsRemaining(null);
  };

  // FOR TESTING - show webcam
  // useKeyPress("s", () => setShowRecordAndPreviewVids(false));
  // useEffect(() => {
  //   if (settings.showWebcamPreview) {
  //     setShowRecordAndPreviewVids(true);
  //     recordWebcam.open();
  //   } else {
  //     setShowRecordAndPreviewVids(false);
  //   }
  //   // eslint-disable-next-line
  // }, [settings.showWebcamPreview]);

  // before recording countdown to start
  useEffect(() => {
    if (countdownToRecord === null) return;
    if (countdownToRecord === 0) {
      recordWebcam.start();
      setRecordTimeRemaining(settings.recordDuration);
    }
    if (countdownToRecord > -1) {
      clearTimeout(currTimeoutRef.current);
      currTimeoutRef.current = setTimeout(
        () => setCountdownToRecord(countdownToRecord - 1),
        1000
      );
    }

    return () => {
      clearTimeout(currTimeoutRef.current);
      currTimeoutRef.current = null;
    };

    // eslint-disable-next-line
  }, [countdownToRecord]);

  useEffect(() => {
    if (resetMinsRemaining === null) return;
    if (resetMinsRemaining <= 0) {
      onDeleteLastRecording();
      setResetMinsRemaining(null);
      window.location.reload();
    } else {
      clearTimeout(currTimeoutRef.current);
      currTimeoutRef.current = setTimeout(
        () => setResetMinsRemaining(resetMinsRemaining - 1),
        60000
      );
    }

    // eslint-disable-next-line
  }, [resetMinsRemaining]);

  // while recording countdown to stop
  useEffect(() => {
    if (recordTimeRemaining === null) return;
    if (recordTimeRemaining === -1) {
      recordWebcam.stop();

      // set the reset mins to default
      setResetMinsRemaining(settings.resetMins);
    } else if (recordTimeRemaining > -1) {
      clearTimeout(currTimeoutRef.current);
      currTimeoutRef.current = setTimeout(
        () => setRecordTimeRemaining(recordTimeRemaining - 1),
        1000
      );
    }

    // eslint-disable-next-line
  }, [recordTimeRemaining]);

  // Grab webcam frame every <X>ms
  useInterval(() => onFrameUpdate(), 100);
  const onFrameUpdate = () => {
    let vid;

    if (
      !showRecordAndPreviewVids ||
      recordWebcam.status === CAMERA_STATUS.CLOSED ||
      recordWebcam.status === CAMERA_STATUS.ERROR
    ) {
      vid = demoVidRef.current;
    } else if (recordWebcam.status === CAMERA_STATUS.PREVIEW) {
      vid = recordWebcam.previewRef.current;
    } else {
      vid = recordWebcam.webcamRef.current;
    }

    if (!vid) return;

    let currFrameCanvas = frameCanvas.current;
    // only create the frame canvas once
    if (!currFrameCanvas) {
      currFrameCanvas = document.createElement("canvas");
      currFrameCanvas.width = settings.OPTIONS.width;
      currFrameCanvas.height = settings.OPTIONS.height;
      frameCanvas.current = currFrameCanvas;
    }

    const frameCanvasCtx = currFrameCanvas.getContext("2d");

    // NB: use vid.videoWidth and vid.videoHeight if you need the actual webcam w & h
    frameCanvasCtx.drawImage(
      vid,
      0,
      0,
      currFrameCanvas.width,
      currFrameCanvas.height
    );

    if (!canvasRef || !canvasRef.current) return;
    const screenCanvas = canvasRef.current;

    // THE MAGIC
    drawLastHumanCanvas({
      currFrameCanvas,
      screenCanvas,
      ...settings,
    });
  };

  const onPlayIntroAudio = () => {
    const audio = audioRef.current;
    const isPlaying = !audio.paused;

    if (isPlaying) {
      audio.pause();
      audio.currentTime = 0;
    } else {
      audio.play();
    }
  };

  const stopAudioIfPlaying = () => {
    const audio = audioRef.current;
    const isPlaying = !audio.paused;

    if (isPlaying) {
      audio.pause();
      audio.currentTime = 0;
    }
  };

  const onRecordStart = () => {
    // allows a few seconds of webcam before recording
    setShowRecordAndPreviewVids(true);
    recordWebcam.retake();
    clearTimeout(currTimeoutRef.current);
    currTimeoutRef.current = setTimeout(() => {
      setCountdownToRecord(settings.secondsBeforeRecord);
    }, 100);
  };

  const onDeleteLastRecording = () => setShowRecordAndPreviewVids(false);

  // const showDemo =
  //   !showRecordAndPreviewVids ||
  //   recordWebcam.status === CAMERA_STATUS.CLOSED ||
  //   recordWebcam.status === CAMERA_STATUS.ERROR;

  return (
    <div className={styles.webcamMirror}>
      {/* <button
        style={{ position: "fixed", zIndex: 999, top: 0, right: 0 }}
        disabled={recordWebcam.status !== CAMERA_STATUS.PREVIEW}
        onClick={recordWebcam.download}
      >
        Download
      </button> */}
      <div style={{ position: "fixed", left: "-100%" }}>
        <audio controls src={introAudio} ref={audioRef} />
      </div>

      <div
        className={styles.content}
        style={{ marginRight: 0 - settings.vertOffset }}
      >
        {countdownToRecord && countdownToRecord > 0 && (
          <CountdownToRecord currentCount={countdownToRecord} />
        )}

        {recordTimeRemaining && recordTimeRemaining >= 0 && (
          <RecordingIndicator secondsRemaining={recordTimeRemaining} />
        )}

        {/* <CountdownToRecord currentCount={3} /> */}
        {/* <RecordingIndicator secondsRemaining={7} /> */}

        <canvas
          ref={canvasRef}
          width={settings.outputCanvasSize.w}
          height={settings.outputCanvasSize.h}
        />
      </div>

      <div className={styles.hidden}>
        {/* {showDemo && ( */}
        <video
          src={"demo.mp4"}
          ref={demoVidRef}
          width={settings.OPTIONS.width}
          autoPlay
          loop
          muted
        />
        {/* )} */}

        <video
          width={settings.OPTIONS.width}
          height={settings.OPTIONS.height}
          ref={recordWebcam.webcamRef}
          autoPlay
          muted
        />

        <video
          width={settings.OPTIONS.width}
          ref={recordWebcam.previewRef}
          autoPlay
          loop
          muted={
            !showRecordAndPreviewVids ||
            recordWebcam.status !== CAMERA_STATUS.PREVIEW
          }
        />
      </div>
    </div>
  );
};
